import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bell, ChevronDown, ChevronRight, LogOut, Search, User, Wallet, X } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import { useAuthStore } from '../stores/authStore'
import { api } from '../services/api'
import { communicationService } from '../services/communicationService'
import { usePermissions } from '../hooks/usePermissions'
import { getSidebarTheme } from '../config/sidebarThemes'
import { UserAvatar } from './account/UserAvatar'
import { displayName as nameOf, roleName } from './account/accountDisplay'

interface SearchResult {
  type: string
  title: string
  subtitle: string
  href: string
}

export default function Header() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(query.trim()), 250)
    return () => window.clearTimeout(timeout)
  }, [query])

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [])

  const search = useQuery({
    queryKey: ['global-search', debouncedQuery],
    queryFn: async () => (await api.get('/auth/search/', { params: { q: debouncedQuery } })).data as { results: SearchResult[] },
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  })
  const unread = useQuery({
    queryKey: ['communication', 'unread-counts'],
    queryFn: communicationService.getUnreadCounts,
    refetchInterval: 60_000,
  })
  const unreadTotal = (unread.data?.unread_messages || 0) + (unread.data?.unread_notifications || 0)

  const selectResult = (href: string) => {
    navigate(href)
    setQuery('')
    setOpen(false)
  }

  const { isStaff } = usePermissions()
  const displayName = nameOf(user)
  const roleLabel = roleName(user?.user_type)
  const workspace = getSidebarTheme(user?.user_type).label
  // "Head of department" alone never says which one -- the same gap the
  // staff directory had, just showing up here too since this is the label
  // people actually see every time they're signed in.
  const headedDepartments = (user?.headed_departments || []).map(dept => dept.name).join(', ')

  return (
    <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 shadow-sm backdrop-blur-sm">
      <div className="flex items-center justify-between gap-4 px-3 py-3 sm:px-6">
        <div ref={containerRef} className="relative min-w-0 w-full max-w-xl">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <input
            value={query}
            onChange={event => { setQuery(event.target.value); setOpen(true) }}
            onFocus={() => setOpen(true)}
            onKeyDown={event => {
              if (event.key === 'Escape') setOpen(false)
              if (event.key === 'Enter' && search.data?.results[0]) selectResult(search.data.results[0].href)
            }}
            placeholder="Search students, staff, books, invoices…"
            aria-label="Search school records"
            aria-expanded={open}
            className="block w-full rounded-md border border-gray-300 bg-white py-2.5 pl-10 pr-9 text-sm placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
          {query && <button onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-2 top-2.5 p-1 text-gray-400 hover:text-gray-700"><X className="h-4 w-4" /></button>}
          {open && debouncedQuery.length >= 2 && <div className="absolute left-0 right-0 top-12 z-50 max-h-[70vh] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl">
            {search.isLoading && <p className="p-4 text-sm text-gray-500">Searching school records…</p>}
            {search.isError && <div className="p-4 text-sm text-red-700">Search is temporarily unavailable. Please try again.</div>}
            {search.data?.results.map((result, index) => <button key={`${result.type}-${result.href}-${index}`} onClick={() => selectResult(result.href)} className="flex w-full items-start gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-0 hover:bg-primary-50 focus:bg-primary-50 focus:outline-none">
              <span className="mt-0.5 rounded bg-gray-100 px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-gray-600">{result.type}</span>
              <span className="min-w-0"><span className="block truncate text-sm font-medium text-gray-900">{result.title}</span><span className="block truncate text-xs text-gray-500">{result.subtitle}</span></span>
            </button>)}
            {!search.isLoading && search.data?.results.length === 0 && <div className="p-5 text-center"><p className="text-sm font-medium text-gray-700">No matching records</p><p className="mt-1 text-xs text-gray-500">Try a name, admission number, invoice, book, or item code.</p></div>}
          </div>}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-2 md:gap-3">
          <Link to="/communication" aria-label={`${unreadTotal} unread messages and notifications`} className="relative rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700">
            <Bell className="h-5 w-5" />
            {unreadTotal > 0 && <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-red-500 px-1 text-center text-[10px] font-semibold leading-5 text-white">{unreadTotal > 99 ? '99+' : unreadTotal}</span>}
          </Link>

          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen(value => !value)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={`Account menu for ${displayName}`}
              className={`flex items-center gap-2.5 rounded-full py-1 pl-1 pr-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 md:rounded-lg md:pr-3 ${menuOpen ? 'bg-gray-100' : 'hover:bg-gray-100'}`}
            >
              <UserAvatar name={displayName} role={user?.user_type} photo={user?.profile_picture} size="sm" />
              <span className="hidden min-w-0 text-left md:block">
                <span className="block max-w-[11rem] truncate text-sm font-semibold leading-5 text-gray-900">{displayName}</span>
                <span className="block max-w-[11rem] truncate text-xs leading-4 text-gray-500">
                  {roleLabel}{headedDepartments && ` · ${headedDepartments}`}
                </span>
              </span>
              <ChevronDown className={`hidden h-4 w-4 text-gray-400 transition-transform md:block ${menuOpen ? 'rotate-180' : ''}`} />
            </button>

            {menuOpen && <div role="menu" aria-label="Account" className="absolute right-0 top-full z-40 mt-2 w-72 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5">
              {/* The button already shows the name and role; this says which account and workspace, once. */}
              <div className="border-b border-gray-100 px-4 py-3">
                <p className="text-xs text-gray-500">Signed in as</p>
                <p className="truncate text-sm font-semibold text-gray-900">{user?.email || user?.username}</p>
                <p className="mt-0.5 truncate text-xs text-gray-500">{roleLabel} · {workspace}</p>
              </div>
              <div className="p-1.5">
                <Link to="/profile" role="menuitem" onClick={() => setMenuOpen(false)} className="group flex items-center gap-3 rounded-lg px-2.5 py-2 transition-colors hover:bg-primary-50 focus-visible:bg-primary-50 focus-visible:outline-none">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700 group-hover:bg-white"><User className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900">My profile</span>
                    <span className="block text-xs text-gray-500">Account details and password</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-300" />
                </Link>
                {isStaff && !user?.must_change_password && (
                  <Link to="/profile#pay" role="menuitem" onClick={() => setMenuOpen(false)} className="group flex items-center gap-3 rounded-lg px-2.5 py-2 transition-colors hover:bg-primary-50 focus-visible:bg-primary-50 focus-visible:outline-none">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700 group-hover:bg-white"><Wallet className="h-4 w-4" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-gray-900">My pay</span>
                      <span className="block text-xs text-gray-500">Payslips and salary advances</span>
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-300" />
                  </Link>
                )}
              </div>
              <div className="border-t border-gray-100 p-1.5">
                <button type="button" role="menuitem" onClick={logout} className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-red-50 hover:text-red-700 focus-visible:bg-red-50 focus-visible:outline-none">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center"><LogOut className="h-4 w-4" /></span>
                  Sign out
                </button>
              </div>
            </div>}
          </div>
        </div>
      </div>
    </header>
  )
}
